/**
 * „Wirkung prüfen“ (Boards „Preisautomatik · Wirkung prüfen“ und „Handy · Wirkung prüfen“): am
 * Desktop ein Dialog, am Handy ein Blatt von unten, beide mit festem Fuß. Zustände: rechnet,
 * gescheitert (Grund als Satz, bei neuerem Serverstand „Neuen Stand laden“), berechnet aber gesperrt
 * (`canActivate=false`, „Erneut versuchen“) und bereit („Version n aktivieren“).
 */
import type { RefObject } from 'react';
import type {
  DynamicPricingMode,
  PricingConfigurationPreviewResponse,
  PricingOwnershipAdoptionRequest,
} from '@/shared/api';
import { BottomSheet, Dialog, DialogBody, DialogContent, DialogTitle } from '@/shared/components';
import { DialogDescription } from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { PricingPreviewDialogFooter } from './PricingPreviewDialogFooter';
import { PricingPreviewState } from './PricingPreviewState';
import { previewState } from './previewModel';
import { MODE_LABEL } from './StatusStrategySection';

interface Props {
  open: boolean;
  apartmentName?: string;
  mode: DynamicPricingMode;
  currency: string;
  /** Nummer der Version, die das Aktivieren anlegt. */
  nextVersion: number;
  horizonDays: number;
  preview?: PricingConfigurationPreviewResponse;
  loading: boolean;
  saving: boolean;
  /** Vorschau oder Aktivierung ist gescheitert; `failure` nennt den Grund als Satz. */
  failed: boolean;
  activationFailed: boolean;
  failure?: string | null;
  /** Ohne Aussicht auf ein anderes Ergebnis fehlt „Erneut versuchen“. */
  retryable: boolean;
  reloadable: boolean;
  ownershipAdoption: PricingOwnershipAdoptionRequest;
  returnFocusRef: RefObject<HTMLElement | null>;
  onOpenChange: (open: boolean) => void;
  onOwnershipAdoptionChange: (value: PricingOwnershipAdoptionRequest) => void;
  onRetry: () => void;
  onConfirm: () => void;
  onReload?: () => void;
}

export function PricingImpactPreviewDialog(props: Props) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const state = previewState({
    loading: props.loading,
    preview: props.preview,
    failed: props.failed,
  });
  const eyebrow = [props.apartmentName, `Version ${props.nextVersion}`, MODE_LABEL[props.mode]]
    .filter(Boolean)
    .join(' · ');
  const description = `Der Server rechnet die neuen Regeln für die nächsten ${props.horizonDays} Tage durch. Noch ändert sich nichts.`;
  const close = (open: boolean) => !props.saving && props.onOpenChange(open);
  const body = <PricingPreviewState state={state} {...props} />;
  const footer = (
    <PricingPreviewDialogFooter
      state={state}
      nextVersion={props.nextVersion}
      saving={props.saving}
      reloadable={props.reloadable}
      retryable={props.retryable}
      onClose={() => close(false)}
      onConfirm={props.onConfirm}
      onRetry={props.onRetry}
      onReload={props.onReload}
    />
  );
  if (mobile) {
    return (
      <BottomSheet
        open={props.open}
        onOpenChange={close}
        title="Wirkung prüfen"
        description={eyebrow}
        footer={footer}
        className="@container"
        returnFocusRef={props.returnFocusRef}
      >
        <div className="flex flex-col gap-3 py-1">{body}</div>
      </BottomSheet>
    );
  }
  return (
    <Dialog open={props.open} onOpenChange={close}>
      <DialogContent
        showClose={!props.saving}
        className="rounded-hero max-h-[calc(100dvh-5rem)] max-w-[900px] overflow-hidden"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          props.returnFocusRef.current?.focus();
        }}
      >
        <div className="border-hairline shrink-0 border-b px-6 py-5 pr-16">
          <p className="text-forest font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
            {eyebrow}
          </p>
          <DialogTitle className="mt-1 font-serif text-[26px] font-bold">
            Wirkung prüfen
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </div>
        <DialogBody className="@container flex flex-col gap-4">{body}</DialogBody>
        <div className="border-hairline shrink-0 border-t px-6 py-4">{footer}</div>
      </DialogContent>
    </Dialog>
  );
}
